import {lazy, useCallback, useMemo} from "react";
import {routePaths} from "../../shared/utils/constants/routePaths";
import {shouldAuthenticate} from "../../shared/utils/guard";
import {useSelector} from "react-redux";
import DefaultLayout from "../../layouts/default/DefaultLayout";
import DigitalContractDetails
    from "../mobile/sub-menu-items/contratto-digitale/digital-contract-details/DigitalContractDetails";
import OrdiniConsip from "../mobile/sub-menu-items/ordini-consip/OrdiniConsip";
import OrdiniConsipDetails from "../mobile/sub-menu-items/ordini-consip/ordini-consip-details/OrdiniConsipDetails";
import MonitoringESim from "../mobile/sub-menu-items/monitoring-esim/MonitoringESim";
import EsitoInvioEmail from "../mobile/sub-menu-items/monitoring-esim/esito-invio-email/EsitoInvioEmail";
import ParametriESim from "../mobile/sub-menu-items/monitoring-esim/parametri-esim/ParametriESim";
import EsitoCambioFormato from "components/mobile/sub-menu-items/monitoring-esim/esito-cambio-formato/EsitoCambioFormato";

const Mobile = lazy(() => import("../mobile/Mobile"))
const OrdiniDiSpedizione = lazy(() => import("../mobile/sub-menu-items/ordini-di-spedizione/OrdiniDiSpedizione"))
const OrderDetails = lazy(() => import("../mobile/sub-menu-items/ordini-di-spedizione/order-details/OrderDetails"))
const Sim = lazy(() => import("../mobile/sub-menu-items/sim/Sim"))
const RichiestaAssistenza = lazy(() => import("../mobile/sub-menu-items/richieste-assistenza/RichiestaAssistenza"))
const Report = lazy(() => import("../mobile/sub-menu-items/report/Report"))
const Device = lazy(() => import("../mobile/sub-menu-items/device/Device"))
const DeviceV2 = lazy(() => import("../mobile/sub-menu-items/device-v2/Device"));
const Manuale = lazy(() => import("../mobile/sub-menu-items/manuale/Manuale"))
const ExtraShare = lazy(() => import("../mobile/sub-menu-items/extra-share/ExtraShare"))
const DettaglioBulk = lazy(() => import("../mobile/sub-menu-items/extra-share/OfferteBulk/DettaglioBulk/DettaglioBulk"))
const SoglieEventi = lazy(() => import("../mobile/sub-menu-items/soglie-eventi/SoglieEventi"))
const ContrattoDigitale = lazy(() => import("../mobile/sub-menu-items/contratto-digitale/ContrattoDigitale"))
const RestituzioniDetails = lazy(() => import('../mobile/sub-menu-items/device-v2/tabs-device/restituzioni/restituzioni-details/RestituzioniDetails'))

const useAppRoutes = () => {
    const token = useSelector((state) => state.authReducer.token)
    const roles = useSelector((state) => state.authReducer.userClaims.roles)
    const typeUser = useSelector((state) => state.authReducer.userClaims.typeUser)
    const isConsipCustomer = useSelector((state) => state.authReducer.userClaims.isConsipCustomer)
    const ep63Flag = useSelector(state => state.authReducer.frontendConfiguration?.ep63) === "1";

    const isAuthenticationRequired =
        window.location.hostname.toLowerCase().includes("mobileenterprise-int.intranet.fw") ||
        window.location.hostname.toLowerCase().includes("mobileenterprise-int.fastweb.it") ||
        window.location.hostname.toLowerCase().includes("mobileenterprise.fastweb.it");

    const getGuard = useCallback((role) => {
        return (!!token && shouldAuthenticate(role, typeUser, roles)) || !isAuthenticationRequired
    }, [token, typeUser, roles, isAuthenticationRequired])

    const routes = useMemo(() => {
        return [
            {
                path: routePaths.MOBILE,
                component: Mobile,
                layout: DefaultLayout,
                guard: getGuard(''),
            },
            {
                path: routePaths.ORDINI_DI_SPEDIZIONE,
                component: OrdiniDiSpedizione,
                layout: DefaultLayout,
                guard: getGuard('A'),
            },
            {
                path: routePaths.ORDINI_DI_SPEDIZIONE_ID,
                component: OrderDetails,
                layout: DefaultLayout,
                guard: getGuard('A'),
            },
            {
                path: routePaths.SIM,
                component: Sim,
                layout: DefaultLayout,
                guard: getGuard('A'),
            },
            {
                path: routePaths.ASSISTENZA,
                component: RichiestaAssistenza,
                layout: DefaultLayout,
                guard: getGuard('C'),
            },
            {
                path: routePaths.REPORT,
                component: Report,
                layout: DefaultLayout,
                guard: getGuard('B'),
            },
            {
                path: routePaths.DEVICE,
                component: ep63Flag ? DeviceV2 : Device,
                layout: DefaultLayout,
                guard: getGuard('A'),
            },
            {
                path: routePaths.MANUALE,
                component: Manuale,
                layout: DefaultLayout,
                guard: getGuard('A'),
            },
            {
                path: routePaths.EXTRA_SHARE,
                component: ExtraShare,
                layout: DefaultLayout,
                guard: getGuard(''),
            },
            {
                path: routePaths.EXTRA_SHARE_BULK_ID,
                component: DettaglioBulk,
                layout: DefaultLayout,
                guard: getGuard(''),
            },
            {
                path: routePaths.THRESHOLD_EVENTS,
                component: SoglieEventi,
                layout: DefaultLayout,
                guard: getGuard(''),
            },
            {
                path: routePaths.CONTRATTO_DIGITALE,
                component: ContrattoDigitale,
                layout: DefaultLayout,
                guard: getGuard(''),
            },
            {
                path: routePaths.CONTRATTO_DIGITALE_DETAILS,
                component: DigitalContractDetails,
                layout: DefaultLayout,
                guard: getGuard('')
            },
            {
                path: routePaths.ORDINI_CONSIP_TM9,
                component: OrdiniConsip,
                layout: DefaultLayout,
                guard: getGuard(''),
                shouldRedirect: !isConsipCustomer,
                redirectTo: routePaths.MOBILE
            },
            {
                path: routePaths.ORDINI_CONSIP_DETAILS,
                component: OrdiniConsipDetails,
                layout: DefaultLayout,
                guard: getGuard(''),
                shouldRedirect: !isConsipCustomer,
                redirectTo: routePaths.MOBILE
            },
            {
                path: routePaths.ESIM,
                component: MonitoringESim,
                layout: DefaultLayout,
                guard: getGuard('')
            },
            {
                path: routePaths.ESIM_INVIO_EMAIL,
                component: EsitoInvioEmail,
                layout: DefaultLayout,
                guard: getGuard('')
            },
            {
                path: routePaths.ESIM_PARAMETRI,
                component: ParametriESim,
                layout: DefaultLayout,
                guard: getGuard('')
            },
            {
                // Here we match the route path to the component we have created
                path: routePaths.ESIM_FORMAT_CHANGE,
                component: EsitoCambioFormato,
                layout: DefaultLayout,
                guard: getGuard('')
            },
            {
                path: routePaths.RESTITUZIONI_DETAILS,
                component: RestituzioniDetails,
                layout: DefaultLayout,
                guard: getGuard('')
            }
        ]
    }, [getGuard, isConsipCustomer])

    return [routes]
}

export default useAppRoutes
